import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Box,
} from '@mui/material';

/**
 * Reusable skeleton loading placeholder for tables.
 *
 * @param {Object} props
 * @param {number} [props.columns=4] - Number of columns
 * @param {number} [props.rows=5] - Number of skeleton rows
 * @param {string[]} [props.headers] - Optional header labels (defaults to skeleton bars)
 * @param {boolean} [props.dense=false] - Use dense table sizing
 */
function TableSkeleton({ columns = 4, rows = 5, headers, dense = false }) {
  const colCount = headers ? headers.length : columns;

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {Array.from({ length: colCount }).map((_, i) => (
              <TableCell key={i}>
                {headers?.[i] ?? <Skeleton width="60%" />}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <TableRow key={rowIdx}>
              {Array.from({ length: colCount }).map((_, colIdx) => (
                <TableCell key={colIdx}>
                  <Skeleton variant="text" width={`${60 + Math.random() * 30}%`} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Card-style skeleton for mobile list views.
 *
 * @param {Object} props
 * @param {number} [props.count=3] - Number of skeleton cards
 */
export function CardSkeleton({ count = 3 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Paper key={i} sx={{ p: 2 }}>
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Skeleton variant="rounded" width={60} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
          </Box>
          <Skeleton variant="rounded" width="100%" height={36} sx={{ mt: 2 }} />
        </Paper>
      ))}
    </Box>
  );
}

export default TableSkeleton;
